import { useState } from "react";
import { BarChart3 } from "lucide-react";
import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

/** B.15: Shared Attribution Reporting — LAUNCH FEATURE
 * Shows split attribution when agencies collaborate on a client.
 * Uses attributionAllocations table (Layer 0.8). */

interface AttributionEntry {
  clientName: string;
  totalRevenue: number;
  myShare: number;
  myPercentage: number;
  partnerName: string;
  partnerShare: number;
  partnerPercentage: number;
  period: string;
}

export default function AgencyAttributionPage() {
  const { t } = useTranslation();
  const [attributions] = useState<AttributionEntry[]>([]);
  const [period, setPeriod] = useState("current");

  const totalRevenue = attributions.reduce((sum, a) => sum + a.myShare, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t(MSG.agency.attribution.title)}</h1>
          <p className="mt-1 text-muted-foreground">{t(MSG.agency.attribution.description)}</p>
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="current">{t(MSG.agency.attribution.period.current)}</option>
          <option value="last">{t(MSG.agency.attribution.period.last)}</option>
          <option value="quarter">{t(MSG.agency.attribution.period.quarter)}</option>
        </select>
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <p className="text-sm text-muted-foreground">{t(MSG.agency.attribution.revenueLabel)}</p>
        <p className="mt-1 text-3xl font-bold text-foreground">€{totalRevenue.toLocaleString()}</p>
      </div>

      {attributions.length === 0 ? (
        <div className="rounded-md border border-border p-12 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 font-medium text-foreground">{t(MSG.agency.attribution.emptyTitle)}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t(MSG.agency.attribution.emptyDescription)}</p>
        </div>
      ) : (
        <div className="rounded-md border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.agency.attribution.table.client)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.agency.attribution.table.totalRevenue)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.agency.attribution.table.yourShare)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.agency.attribution.table.partner)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.agency.attribution.table.split)}</th>
              </tr>
            </thead>
            <tbody>
              {attributions.map((a, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{a.clientName}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">€{a.totalRevenue.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    €{a.myShare.toLocaleString()} ({a.myPercentage}%)
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{a.partnerName}</td>
                  <td className="px-4 py-3">
                    <div className="flex h-2 overflow-hidden rounded-full bg-muted">
                      <div className="bg-primary" style={{ width: `${a.myPercentage}%` }} />
                      <div className="bg-muted-foreground/30" style={{ width: `${a.partnerPercentage}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
